import { useReferenceStore } from '@/store/reference.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { shouldAcceptReferenceBroadcast } from '@/lib/services/reference-tenant-cache.js';

const CHANNEL_NAME = 'sgrc-reference';
const LOCK_SCOPE_PREFIX = 'sgrc-reference-refresh:';

/**
 * Cross-tab leadership for soft refreshes.
 * @param {string} scope
 * @param {() => Promise<unknown>} fn
 * @param {{ ifAvailable?: boolean }} [opts] — force paths should pass ifAvailable:false or skip this helper
 */
export async function withRefreshLeadership(scope, fn, { ifAvailable = true } = {}) {
    if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
        return fn();
    }
    const name = `${LOCK_SCOPE_PREFIX}${scope}`;
    if (!ifAvailable) {
        return navigator.locks.request(name, async () => fn());
    }
    return navigator.locks.request(
        name,
        { ifAvailable: true },
        async (lock) => {
            if (lock == null) return undefined;
            return fn();
        },
    );
}

/**
 * Apply tenant_reset from another tab by syncing selected tenant with fromBroadcast
 * so subscribeTenantChange listeners (reference reset + domain refetch) run once,
 * without re-broadcasting tenant_reset.
 */
function handleTenantResetFromBroadcast(msg) {
    const incomingTenantId = msg?.tenantId || null;
    if (!incomingTenantId) return;

    useTenantStore.getState().setSelectedTenantId(incomingTenantId, { fromBroadcast: true });
}

class ReferenceBroadcast {
    constructor() {
        this._channel = null;
        this._initialized = false;
    }

    init() {
        if (this._initialized) return;
        if (typeof BroadcastChannel === 'undefined') return;

        try {
            this._channel = new BroadcastChannel(CHANNEL_NAME);
            this._channel.onmessage = (event) => this._onMessage(event.data);
            this._initialized = true;
        } catch {
            // BroadcastChannel not supported
        }
    }

    send(message) {
        if (!this._channel) return;
        try {
            this._channel.postMessage(message);
        } catch {
            // Silently fail
        }
    }

    _onMessage(msg) {
        if (!msg || !msg.type) return;

        if (msg.type === 'tenant_reset') {
            handleTenantResetFromBroadcast(msg);
            return;
        }

        const selectedTenantId = useTenantStore.getState().selectedTenantId;
        if (!shouldAcceptReferenceBroadcast(msg.tenantId, selectedTenantId)) {
            return;
        }

        const store = useReferenceStore.getState();

        if (msg.type === 'resolved' && msg.entity && Array.isArray(msg.items) && msg.items.length > 0) {
            store.mergeResolvedItems(msg.entity, msg.items);
            return;
        }

        if (msg.type === 'seed' && msg.data) {
            for (const [entity, payload] of Object.entries(msg.data)) {
                if (payload && payload.items) {
                    store.setSeedData(entity, payload.items, payload.version);
                }
            }
            return;
        }

        if ((msg.type === 'bulk' || msg.type === 'entity') && msg.data) {
            const validData = {};
            for (const [entity, payload] of Object.entries(msg.data)) {
                if (payload && payload.items) {
                    validData[entity] = payload;
                }
            }
            if (Object.keys(validData).length > 0) {
                store.setMultipleEntities(validData, msg.versions || {});
            }
        }
    }

    /**
     * @param {string|null|undefined} [tenantId] — active tenant after switch (for other tabs)
     */
    notifyTenantReset(tenantId) {
        this.send({
            type: 'tenant_reset',
            tenantId: tenantId || useTenantStore.getState().selectedTenantId || null,
            timestamp: Date.now(),
        });
    }

    destroy() {
        if (this._channel) {
            this._channel.close();
            this._channel = null;
        }
        this._initialized = false;
    }
}

export const referenceBroadcast = new ReferenceBroadcast();
